/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("TooManyFunctions")

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import systems.frictionless.impulse.browser.ErrorTranslation
import systems.frictionless.impulse.browser.composable.component.CenteredOnScreen
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.InlinedErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.viewmodel.DeleteAccountViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState

@Composable
fun DeleteAccount(
    deleteAccountViewModel: DeleteAccountViewModel,
    onForgotPassword: () -> Unit,
    onAccountDeleted: () -> Unit
) {
    val (prevBearerAuth, setBearerAuth) = remember { mutableStateOf<Authentication.Bearer?>(null) }

    when (val deleteAccountSnapshot = deleteAccountViewModel.state.collectAsState().value) {
        is DeleteAccountViewModel.State.Overview -> DeleteContainer { Overview { deleteAccountViewModel.transition() } }
        is DeleteAccountViewModel.State.SignIn ->
            DeleteContainer {
                LoginForm(deleteAccountSnapshot.loginViewModel, onForgotPassword) {
                    setBearerAuth(it.authentication)
                    deleteAccountViewModel.transition()
                }
            }
        is DeleteAccountViewModel.State.Delete ->
            DeleteContainer {
                DeleteAccountSection(
                    deleteAccountSnapshot,
                    prevBearerAuth!!
                ) { deleteAccountViewModel.transition() }
            }
        is DeleteAccountViewModel.State.Done -> DeleteContainer { Done(onAccountDeleted) }
    }
}

@Composable
private fun DeleteContainer(mainContent: @Composable () -> Unit) {
    ContentContainer(
        navigationSection = null,
        mainContainerCssClass = AppStyleSheet.login,
        mainContentContainerId = HtmlId.PageDeleteAccount,
        mainContent = {
            CenteredOnScreen {
                mainContent()
            }
        }
    )
}

@Composable
private fun Overview(onSubmit: () -> Unit) {
    Header("Account Deletion")
    P {
        Text("To delete your account, you'll first need to sign in.  After signing in, you'll confirm deleting your account.")
    }

    Div {
        Br {}
        Button(
            attrs = {
                classes(AppStyleSheet.blueButton)
                id(HtmlId.PrimaryButton.idString)
                onClick { onSubmit() }
            }
        ) {
            Text("Next")
        }
    }
}

@Composable
private fun DeleteAccountSection(
    viewModel: DeleteAccountViewModel.State.Delete,
    authentication: Authentication,
    onAccountDeleted: () -> Unit
) {
    val composableScope = rememberCoroutineScope()

    val startDeleteRequest = {
        viewModel.startDeleteRequest(
            composableScope,
            authentication
        )
    }

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> DeleteAccountForm(null, startDeleteRequest)
        is NetworkViewModelState.Busy -> DeleteAccountProgress()
        is NetworkViewModelState.Failure -> {
            val translation =
                ErrorTranslation.translate(state.failure) {
                    "Server error occurred"
                }
            DeleteAccountForm(translation, startDeleteRequest)
        }
        is NetworkViewModelState.Complete -> onAccountDeleted()
    }
}

@Composable
private fun DeleteAccountForm(
    errorMessage: String?,
    onSubmit: () -> Unit
) {
    Header("Confirm account deletion")
    P {
        Text("All data associated with your account will be deleted immediately. This cannot be undone.")
    }

    val submitForm: () -> Unit = { onSubmit() }

    Div {
        errorMessage?.let {
            InlinedErrorMessage(it)
        }
        Br {}
        Button(
            attrs = {
                classes(AppStyleSheet.blueButton)
                id(HtmlId.PrimaryButton.idString)
                onClick {
                    submitForm()
                }
            }
        ) {
            Text("Delete Account")
        }
    }
}

@Composable
private fun Done(onSubmit: () -> Unit) {
    Header("Your account has been deleted")

    Div {
        Br {}
        Button(
            attrs = {
                classes(AppStyleSheet.blueButton)
                id(HtmlId.PrimaryButton.idString)
                onClick { onSubmit() }
            }
        ) {
            Text("Next")
        }
    }
}

@Composable
private fun Header(headerText: String) {
    H1 {
        Text(headerText)
    }
}

@Composable
private fun DeleteAccountProgress() {
    LoadingIndicator()
}
